import * as React from 'react'

interface State {
  hasError: boolean
}

/**
 * 全域錯誤邊界：UI 不暴露 error.message 給用戶（避免內部變數名/stack 外洩）。
 * 詳細錯誤透過 console.error 與監控管道（後續可接 Sentry）給開發者。
 */
export class GlobalErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // 開發者保留完整 stack；生產環境亦輸出供 console 追蹤（可接 Sentry / 其他遙測）
    console.error('[GlobalErrorBoundary]', error, info)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    // 通用文案：不依賴 i18n（避免 i18n 本身錯誤造成 boundary 二度 crash）
    // 顯示 zh-TW + en 雙語固定文案，不暴露任何內部錯誤細節
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--color-cream)' }}
      >
        <div className="text-center border-2 border-[var(--color-coral)] bg-[var(--color-surface)] shadow-[8px_8px_0_0_var(--color-coral)] p-12 max-w-lg">
          <div className="font-pixel text-[32px] text-[var(--color-coral)] mb-2">⚠</div>
          <div className="font-pixel text-[10px] text-[var(--color-muted)] tracking-widest mb-4">
            SOMETHING WENT WRONG
          </div>
          <p className="text-[14px] text-[var(--color-foreground)] mb-2">
            發生暫時性問題，請重新載入頁面試試。
          </p>
          <p className="text-[12px] text-[var(--color-muted)] mb-6">
            We hit a temporary issue. Please reload the page and try again.
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false })
              window.location.href = '/'
            }}
            className="px-6 py-3 font-pixel text-[9px] tracking-wider border-2 border-[var(--color-coral)] text-[var(--color-coral)] hover:bg-[var(--color-coral)] hover:text-white transition-colors"
          >
            重新載入 / Reload
          </button>
        </div>
      </div>
    )
  }
}
